import { Col, Form } from 'antd'
import React, { FunctionComponent, memo } from 'react'

import {
  BUYBACK_FIELD as FIELD,
  BuybackDebtItem,
  BuybackProcessCostItem,
} from '../../../common/buybackTypes'
import {
  transformDebtsToData,
  transformProcessCostsToData,
} from '../../utils/buybackPayoutHelpersNew'
import { useToggleState } from '../../utils/useToggleState'
import { BuybackFormPayoutBalanceTable } from '../BuybackFormPayoutBalanceTable/BuybackFormPayoutBalanceTable'
import { BuybackFormPayoutChart } from '../BuybackFormPayoutChart/BuybackFormPayoutChart'
import { BuybackFormTitle } from '../BuybackFormTitle/BuybackFormTitle'
import { Fade } from '../Fade/Fade'

export interface BuybackFormPayoutBreakdownProps {
  toggleShow: () => void
}

const requiredFields = [
  FIELD.PropertyPrice,
  FIELD.OfferPrice,
  FIELD.RequestedAmount,

  FIELD.CommissionAmount,
  FIELD.BaseCommissionAmount,

  FIELD.Debts,
  FIELD.BaseDebtFeeTotal,
  FIELD.DebtFeeTotal,
  FIELD.DebtAmountTotal,

  FIELD.ProcessCostAmountTotal,
  FIELD.ProcessCosts,
] as const

export const BuybackFormPayoutBreakdown: FunctionComponent<BuybackFormPayoutBreakdownProps> =
  memo(props => {
    const [showChart, toggleChart] = useToggleState(true)
    const [showTable, toggleTable] = useToggleState(true)
    const [showHelp, toggleHelp] = useToggleState(false)

    return (
      <>
        <BuybackFormTitle
          title="Platební bilance"
          action={{
            onClose: props.toggleShow,
            help: { active: showHelp, onToggle: toggleHelp },
            chart: { active: showChart, onToggle: toggleChart },
            table: { active: showTable, onToggle: toggleTable },
          }}
        />

        <Col span={24}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              requiredFields.some(f => prev[f] !== next[f])
            }
          >
            {({ getFieldsValue }) => {
              const values = getFieldsValue([...requiredFields]) as Record<
                string,
                unknown
              >

              const debts = (values[FIELD.Debts] || []) as BuybackDebtItem[]
              const processCosts = (values[FIELD.ProcessCosts] ||
                []) as BuybackProcessCostItem[]

              const propertyPrice = Number(values[FIELD.PropertyPrice] ?? 0)
              const offerPrice = Number(values[FIELD.OfferPrice] ?? 0)
              const baseCommission = Number(
                values[FIELD.BaseCommissionAmount] ?? 0
              )
              const commission = values[FIELD.CommissionAmount] as
                | number
                | undefined

              const debtTotal = Number(values[FIELD.DebtAmountTotal] ?? 0)
              const baseDebtFeeTotal = Number(
                values[FIELD.BaseDebtFeeTotal] ?? 0
              )
              const debtFeeTotal = values[FIELD.DebtFeeTotal] as
                | number
                | undefined

              const processCostTotal = Number(
                values[FIELD.ProcessCostAmountTotal] ?? 0
              )
              const requested = Number(values[FIELD.RequestedAmount] ?? 0)

              const debtData = transformDebtsToData(debts)
              const processData = transformProcessCostsToData(processCosts)

              const totalOutcome =
                processCostTotal +
                debtTotal +
                (typeof debtFeeTotal === 'number'
                  ? debtFeeTotal
                  : baseDebtFeeTotal) +
                (typeof commission === 'number' ? commission : baseCommission)

              return (
                <div>
                  <Fade show={showChart}>
                    <BuybackFormPayoutChart
                      offerPrice={offerPrice}
                      commission={
                        typeof commission === 'number'
                          ? commission
                          : baseCommission
                      }
                      debtFeeTotal={
                        typeof debtFeeTotal === 'number'
                          ? debtFeeTotal
                          : baseDebtFeeTotal
                      }
                      debtAmountTotal={debtTotal}
                      processCostTotal={processCostTotal}
                      requested={requested}
                    />
                  </Fade>

                  <Fade show={showTable}>
                    <BuybackFormPayoutBalanceTable
                      propertyPrice={propertyPrice}
                      offerPrice={offerPrice}
                      totalOutcome={totalOutcome}
                      requested={requested}
                      debts={debtData}
                      processCosts={processData}
                      commission={commission}
                      baseCommission={baseCommission}
                      debtFeeTotal={debtFeeTotal}
                      debtAmountTotal={debtTotal}
                      processCostTotal={processCostTotal}
                      baseDebtFeeTotal={baseDebtFeeTotal}
                    />
                  </Fade>
                </div>
              )
            }}
          </Form.Item>
        </Col>
      </>
    )
  })
